import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import type { PackageOption_1_0_1 } from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'
import { convertDateByTimezone } from '@luxuryescapes/lib-refunds/build/lib/formatDate'
import { filterDefinedProperties } from 'analytics/snowplow/filterDefinedProperties'
import {
  getCategoryAndProductId,
  getPropertyRateOfferCategoryAndProductId,
} from 'analytics/snowplow/helpers/itemCategorisation'
import sanitizeItemContextMetadata from 'analytics/snowplow/eventContexts/item/sanitizeItemContextMetadata'
import { countOccupants } from 'lib/offer/occupancyUtils'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'

/**
 * Package cards render cash prices with `FormatCurrency`'s `roundedDollar`
 * format. Keep the numeric analytics representation in that same unit, rather
 * than exposing arithmetic precision such as 2672.6899999999996.
 */
function roundPackageCardPrice(price: number): number {
  return Math.round(price)
}

export const getBedbankRateItemContext = (
  offer: App.BedbankOffer | App.BedbankOfferSummary,
  rate: App.BedbankRate,
  price: number,
  value: number,
  checkIn: string,
  checkOut: string,
  occupancy: Array<App.Occupants>,
  currency: string,
) => {
  const offerId = offer.id
  const rateId = rate.id
  const propertyFees = rate.totals.propertyFees ?? 0
  const {
    adults: numberOfAdults,
    children: numberOfChildren,
    infants: numberOfInfants,
  } = countOccupants(occupancy)
  const { categoryId, productId } = getCategoryAndProductId(offer.parentType, offer)
  // Use the all-inclusive totals because they match the price shown for the selected rate.
  // `inclusive` and `memberInclusive` deliberately exclude property fees.
  const memberPriceRaw = (rate.totals.memberTotal ?? 0) > 0 ? rate.totals.memberTotal : undefined
  const nonMemberPrice = rate.totals.total ?? 0
  const memberPrice = memberPriceRaw ?? nonMemberPrice

  return filterDefinedProperties([
    comluxgroup.createItem_1_1_2({
      offerId,
      categoryId,
      productId,
      typeId: rate.roomId,
      price: roundPackageCardPrice(price),
      value: roundPackageCardPrice(value),
      propertyFees,
      currency,
      travelStart: checkIn,
      travelEnd: checkOut,
      numberOfAdults,
      numberOfChildren,
      numberOfInfants,
      rateId,
      metadata: sanitizeItemContextMetadata({
        memberPrice: roundPackageCardPrice(memberPrice),
        nonMemberPrice: roundPackageCardPrice(nonMemberPrice),
        mx: rate.mx,
        pricingMethod: rate.calculationMethod,
      }),
    }),
  ])
}

export const getHotelRateItemContext = (
  offer: App.HotelOffer,
  packageView: App.OfferPackageOptionView,
  displayPrice: number,
  checkIn: string | undefined,
  checkOut: string | undefined,
  occupancy: Array<App.Occupants>,
  currency: string,
  displayMemberPrice?: number,
  displayNonMemberPrice?: number,
  displayValue?: number,
) => {
  const { package: pkg } = packageView
  const {
    adults: numberOfAdults,
    children: numberOfChildren,
    infants: numberOfInfants,
  } = countOccupants(occupancy)
  const { categoryId, productId } = getCategoryAndProductId(offer.parentType, offer)
  const memberPriceRaw = packageView.hotelMemberTotal > 0 ? packageView.hotelMemberTotal : undefined
  const nonMemberPrice = packageView.hotelTotal
  const memberPrice = displayMemberPrice ?? memberPriceRaw ?? nonMemberPrice
  const displayedNonMemberPrice = displayNonMemberPrice ?? nonMemberPrice

  return filterDefinedProperties([
    comluxgroup.createItem_1_1_2({
      offerId: offer.id,
      categoryId,
      productId,
      typeId: pkg.roomType.id,
      rateId: pkg.roomRate.id,
      price: roundPackageCardPrice(displayPrice),
      value: roundPackageCardPrice(displayValue ?? packageView.hotelValue),
      propertyFees: packageView.propertyFees,
      currency,
      ...(checkIn ? { travelStart: checkIn } : {}),
      ...(checkOut ? { travelEnd: checkOut } : {}),
      numberOfAdults,
      numberOfChildren,
      numberOfInfants,
      metadata: sanitizeItemContextMetadata({
        memberPrice: roundPackageCardPrice(memberPrice),
        nonMemberPrice: roundPackageCardPrice(displayedNonMemberPrice),
        mx: pkg.mx,
      }),
    }),
  ])
}

export const getHotelRateContext = (packageView: App.OfferPackageOptionView) => {
  const { package: pkg } = packageView
  const inclusions = [
    ...(pkg.luxPlusInclusionsByTier ? Object.values(pkg.luxPlusInclusionsByTier).flatMap(inclusions => inclusions ?? []) : []),
    ...(pkg.inclusions ?? []),
    ...pkg.bonusInclusions,
  ].map(inclusion => inclusion.description)

  return filterDefinedProperties([
    comluxgroup.createPackageOption_1_0_1({
      id: pkg.id,
      cancellationPolicy: {},
      inclusions,
    }),
  ])
}

export function getPropertyRateItemContext(
  occupancy: Array<App.Occupants>,
  checkIn: string | undefined,
  checkOut: string | undefined,
  rate: App.EnrichedPropertyRate,
  displayPrice?: number,
  displayMemberPrice?: number,
  displayNonMemberPrice?: number,
  displayValue?: number,
) {
  const { offerId, categoryId, productId } = getPropertyRateOfferCategoryAndProductId(rate)
  const {
    adults: numberOfAdults,
    children: numberOfChildren,
    infants: numberOfInfants,
  } = countOccupants(occupancy)
  const nonMemberPrice = displayNonMemberPrice ?? rate.totals.displayPrice
  const memberPrice = displayMemberPrice ?? rate.luxPlusTotals?.displayPrice ?? nonMemberPrice
  const primaryPrice = displayPrice ?? memberPrice
  // HP-1329 previously reconstructed a hotel-only amount by subtracting cheapestFlightPrice from
  // sellingPrice. That no longer represents the package card after flight, optional-bundle, GHA,
  // metasearch and LuxPlus display rules have resolved, so callers now supply those displayed
  // totals and the rate totals are only fallbacks.
  // This is still consistent with getBedbankRateItemContext at the event-contract level: both emit
  // rounded cash amounts shown on the card. It intentionally replaces the old consistency rule of
  // reporting reconstructed hotel-only prices that excluded separately represented property fees.
  const pricingMethod = rate.service.type === 'bedbank' ? rate.service.pricingMethod : undefined

  return filterDefinedProperties([
    comluxgroup.createItem_1_1_2({
      offerId,
      categoryId,
      productId,
      typeId: rate.roomId,
      rateId: rate.service.rateId,
      ...(checkIn ? { travelStart: checkIn } : {}),
      ...(checkOut ? { travelEnd: checkOut } : {}),
      numberOfAdults,
      numberOfChildren,
      numberOfInfants,
      value: roundPackageCardPrice(displayValue ?? rate.totals.valuedUpToPrice),
      price: roundPackageCardPrice(primaryPrice),
      propertyFees: rate.totals.propertyFees,
      currency: rate.currency,
      metadata: sanitizeItemContextMetadata({
        memberPrice: roundPackageCardPrice(memberPrice),
        nonMemberPrice: roundPackageCardPrice(nonMemberPrice),
        mx: rate.totals.mx,
        pricingMethod,
      }),
    }),
  ])
}

function getRateListName(type: 'bedbank' | 'property'): string {
  switch (type) {
    case 'bedbank':
      return 'Bedbank'
    case 'property':
      return 'SPP'
    default:
      assertUnreachable(type)
  }
}

export const getRateListContext = (
  type: 'bedbank' | 'property',
  ratePosition: number,
  ratePositionReason: string | undefined,
  rateListId: string,
  roomPosition: number,
  roomListId: string,
) => {
  const name = getRateListName(type)
  return filterDefinedProperties([
    comluxgroup.createListItem_1_1_0({
      itemPosition: ratePosition,
      positionReason: ratePositionReason,
      listInstance: rateListId,
      listName: `PackageOptionList - Hotels - ${name} rates`,
    }),
    comluxgroup.createListItem_1_0_0({
      itemPosition: roomPosition,
      listInstance: roomListId,
      listName: `PackageList - Hotels - ${name} rooms`,
    }),
  ])
}

export const getBedbankRateContext = (
  rate: App.BedbankRate,
  isFlightBundle: boolean, // rate.isFlightBundle is not reliable, so we pass it explicitly
) => {
  const rateId = rate.id

  const cancellationPolicy: PackageOption_1_0_1['cancellationPolicy'] = {
    refundable: rate.refundable,
    partiallyRefundable: rate.partiallyRefundable,
    penalties: rate.cancellationPolicy.policies.flatMap((cp) => {
      try {
        const start = convertDateByTimezone(new Date(cp.start), cp.timezone).toISOString()
        const end = convertDateByTimezone(new Date(cp.end), cp.timezone).toISOString()
        return [{
          start,
          end,
          ...(cp.amount ? { amount: cp.amount } : {}),
          ...(cp.nights ? { nights: cp.nights } : {}),
          ...(cp.percent ? { percent: cp.percent } : {}),
          currency: cp.currency,
        }]
      } catch {
        // can't parse the date, skip this entry
        return []
      }
    }),
  }

  const inclusions = rate.inclusions.map(x => x.description)

  return filterDefinedProperties([
    comluxgroup.createPackageOption_1_0_2({
      id: rateId,
      supplier: rate.supplierCode,
      cancellationPolicy,
      inclusions,
      isFlightBundle,
      boardCode: rate.boardCode,
    }),
  ])
}

function getPropertyRateSupplier(rate: App.EnrichedPropertyRate) {
  switch (rate.service.type) {
    case 'lpc':
      return 'lpc'
    case 'bedbank':
      return rate.service.supplierCode
    default:
      assertUnreachable(rate.service)
  }
}

// TODO(Xiao): This needs to be updated once I fix up the `BasePropertyRate` type.
export function getPropertyRateContext(rate: App.EnrichedPropertyRate) {
  // We'll order these roughly by how they'd be displayed.
  const allInclusions = [
    rate.luxPlusInclusions,
    rate.bonusInclusions,
    rate.inclusions,
    rate.bundleInclusions,
  ].flatMap(inclusions => inclusions.map(({ description }) => description))
  return filterDefinedProperties([
    comluxgroup.createPackageOption_1_0_3({
      id: rate.service.rateId,
      supplier: getPropertyRateSupplier(rate),
      cancellationPolicy: {
        refundable: rate.headlineCancellationPolicyType === 'fullyRefundable',
        partiallyRefundable: rate.headlineCancellationPolicyType === 'partiallyRefundable',
        penalties: rate.cancellationPolicies.map(({
          startIsoDateTime,
          endIsoDateTime,
          penaltyPrice,
        }) => ({
          start: startIsoDateTime,
          end: endIsoDateTime,
          currency: rate.currency,
          amount: penaltyPrice,
        })),
      },
      inclusions: allInclusions,
      isFlightBundle: rate.includesFlights,
      boardCode: rate.boardCode,
      mergedRates: rate.mergedRates,
      isAutoLuxplus: rate.service.type === 'lpc' && rate.service.isAutoLuxPlus,
    }),
  ])
}
